import React, { useState, useEffect } from 'react';
import {
  Create,
  Visibility,
  WorkOutline,
  Done,
  PendingActions,
  TrendingUp,
  TrendingDown,
  AttachMoney,
  Person,
  Assignment,
  CheckCircle,
  Error,
  Warning,
  Info,
  CalendarToday,
  AccessTime,
  Star,
  StarBorder,
     Refresh,
   Download,
   Upload,
   Add,
  Schedule,
  TrendingFlat,
  Speed,
  Assessment,
  BarChart,
  PieChart,
  Timeline,
     Settings,
   Help,
  Dashboard as DashboardIcon
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { designerDesignCaseApi } from '../../services/designer/designCaseApi';
import { useAuth } from '../../contexts/AuthContext';

const DesignerDashboard = () => {
  const { user } = useAuth();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalCases: 0,
    pendingCases: 0,
    inProgressCases: 0,
    completedCases: 0,
    totalEarnings: 0,
    averageRating: 0,
    completionRate: 0,
    onTimeDelivery: 0
  });
  const [recentCases, setRecentCases] = useState([]);
  const [upcomingDeadlines, setUpcomingDeadlines] = useState([]);
  const [performanceData, setPerformanceData] = useState({
    monthlyEarnings: [],
    caseCompletion: [],
    ratings: []
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const token = sessionStorage.getItem('token');
      
      if (!token) {
        toast.error('Vui lòng đăng nhập lại');
        return;
      }

      // Fetch designer cases
      const response = await designerDesignCaseApi.getMyCases(token);
      const allCases = response.data || [];
      setCases(allCases);

      // Calculate stats
      const stats = calculateStats(allCases);
      setStats(stats);

      // Get recent cases (last 5)
      setRecentCases(allCases.slice(0, 5));

      // Get upcoming deadlines (next 7 days)
      const upcoming = allCases.filter(case_ => {
        const deadline = new Date(case_.deadline);
        const now = new Date();
        const diffTime = deadline - now;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays >= 0 && diffDays <= 7 && case_.status !== 'approved';
      });
      setUpcomingDeadlines(upcoming.slice(0, 3));

      setLoading(false);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Không thể tải dữ liệu dashboard');
      setLoading(false);
    }
  };

  const calculateStats = (cases) => {
    const totalCases = cases.length;
    const pendingCases = cases.filter(c => c.status === 'pending').length;
    const inProgressCases = cases.filter(c => c.status === 'in_progress').length;
    const completedCases = cases.filter(c => c.status === 'approved').length;
    const totalEarnings = cases.reduce((sum, c) => sum + (c.cost_per_design || 0), 0);
    
    const completedCasesForRate = cases.filter(c => c.status === 'approved' || c.status === 'rejected');
    const completionRate = totalCases > 0 ? (completedCasesForRate.length / totalCases) * 100 : 0;
    
    const onTimeCases = cases.filter(c => {
      if (c.status === 'approved' && c.completed_at) {
        const completed = new Date(c.completed_at);
        const deadline = new Date(c.deadline);
        return completed <= deadline;
      }
      return false;
    });
    const onTimeDelivery = completedCases > 0 ? (onTimeCases.length / completedCases) * 100 : 0;

    return {
      totalCases,
      pendingCases,
      inProgressCases,
      completedCases,
      totalEarnings,
      averageRating: 4.5, // Mock data
      completionRate: Math.round(completionRate),
      onTimeDelivery: Math.round(onTimeDelivery)
    };
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'waiting_approval':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'approved':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'rejected':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <PendingActions className="w-4 h-4" />;
      case 'in_progress':
        return <WorkOutline className="w-4 h-4" />;
      case 'waiting_approval':
        return <Info className="w-4 h-4" />;
      case 'approved':
        return <CheckCircle className="w-4 h-4" />;
      case 'rejected':
        return <Error className="w-4 h-4" />;
      default:
        return <Assignment className="w-4 h-4" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending':
        return 'Chờ nhận';
      case 'in_progress':
        return 'Đang làm';
      case 'waiting_approval':
        return 'Chờ duyệt';
      case 'approved':
        return 'Hoàn thành';
      case 'rejected':
        return 'Từ chối';
      default:
        return 'Không xác định';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('vi-VN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('vi-VN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDaysUntilDeadline = (deadline) => {
    const deadlineDate = new Date(deadline);
    const now = new Date();
    const diffTime = deadlineDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getDeadlineColor = (deadline) => {
    const days = getDaysUntilDeadline(deadline);
    if (days < 0) return 'text-red-600';
    if (days <= 1) return 'text-orange-600';
    if (days <= 3) return 'text-yellow-600';
    return 'text-green-600';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Đang tải dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 sm:p-6 lg:p-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-7xl mx-auto"
      >


        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            whileHover={{ scale: 1.05 }}
            className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-4 sm:p-6 text-white shadow-xl"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm font-medium">Tổng Cases</p>
                <p className="text-2xl sm:text-3xl font-bold">{stats.totalCases}</p>
                <p className="text-blue-200 text-xs mt-1">Tất cả thời gian</p>
              </div>
              <Assignment className="text-2xl sm:text-4xl text-blue-200" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            whileHover={{ scale: 1.05 }}
            className="bg-gradient-to-br from-green-500 to-emerald-500 rounded-2xl p-4 sm:p-6 text-white shadow-xl"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm font-medium">Đã hoàn thành</p>
                <p className="text-2xl sm:text-3xl font-bold">{stats.completedCases}</p>
                <p className="text-green-200 text-xs mt-1">{stats.completionRate}% tỷ lệ</p>
              </div>
              <Done className="text-2xl sm:text-4xl text-green-200" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            whileHover={{ scale: 1.05 }}
            className="bg-gradient-to-br from-yellow-500 to-orange-500 rounded-2xl p-4 sm:p-6 text-white shadow-xl"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-yellow-100 text-sm font-medium">Đang làm</p>
                <p className="text-2xl sm:text-3xl font-bold">{stats.inProgressCases}</p>
                <p className="text-yellow-200 text-xs mt-1">Cases hiện tại</p>
              </div>
              <WorkOutline className="text-2xl sm:text-4xl text-yellow-200" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            whileHover={{ scale: 1.05 }}
            className="bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl p-4 sm:p-6 text-white shadow-xl"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm font-medium">Tổng thu nhập</p>
                <p className="text-xl sm:text-2xl font-bold">{formatCurrency(stats.totalEarnings)}</p>
                <p className="text-purple-200 text-xs mt-1">Tất cả thời gian</p>
              </div>
              <AttachMoney className="text-2xl sm:text-4xl text-purple-200" />
            </div>
          </motion.div>
        </div>

        {/* Performance Metrics */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Rating & Performance */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="bg-white rounded-2xl shadow-xl p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-800">Đánh giá & Hiệu suất</h3>
              <Star className="text-yellow-500" />
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Đánh giá trung bình</span>
                <div className="flex items-center space-x-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star key={star} className={`w-4 h-4 ${star <= stats.averageRating ? 'text-yellow-500' : 'text-gray-300'}`} />
                  ))}
                  <span className="ml-2 text-sm font-semibold">{stats.averageRating}/5</span>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Tỷ lệ hoàn thành</span>
                <span className="text-green-600 font-semibold">{stats.completionRate}%</span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Giao đúng hạn</span>
                <span className="text-blue-600 font-semibold">{stats.onTimeDelivery}%</span>
              </div>
            </div>
          </motion.div>

          {/* Recent Cases */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="bg-white rounded-2xl shadow-xl p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-800">Cases gần đây</h3>
              <Assignment className="text-blue-500" />
            </div>
            
            <div className="space-y-3">
              {recentCases.map((case_, index) => (
                <div key={case_.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                      <span className="text-white text-xs font-bold">{case_.id}</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-800">
                        {case_.product_name || `Case #${case_.id}`}
                      </p>
                      <p className="text-xs text-gray-500">
                        {formatDate(case_.created_at)}
                      </p>
                    </div>
                  </div>
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(case_.status)}`}>
                    {getStatusIcon(case_.status)}
                    <span className="ml-1">{getStatusText(case_.status)}</span>
                  </span>
                </div>
              ))}
              
              {recentCases.length === 0 && (
                <div className="text-center py-4">
                  <Assignment className="text-gray-300 text-2xl mx-auto mb-2" />
                  <p className="text-gray-500 text-sm">Chưa có cases nào</p>
                </div>
              )}
            </div>
          </motion.div>

          {/* Upcoming Deadlines */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="bg-white rounded-2xl shadow-xl p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-800">Deadlines sắp tới</h3>
              <Schedule className="text-red-500" />
            </div>
            
            <div className="space-y-3">
              {upcomingDeadlines.map((case_, index) => {
                const daysUntil = getDaysUntilDeadline(case_.deadline);
                return (
                  <div key={case_.id} className="p-3 bg-red-50 border border-red-200 rounded-xl">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-800">
                        {case_.product_name || `Case #${case_.id}`}
                      </span>
                      <span className={`text-xs font-semibold ${getDeadlineColor(case_.deadline)}`}>
                        {daysUntil < 0 ? 'Quá hạn' : `${daysUntil} ngày`}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>Deadline: {formatDateTime(case_.deadline)}</span>
                      <span className={`font-semibold ${getDeadlineColor(case_.deadline)}`}>
                        {formatCurrency(case_.cost_per_design || 0)}
                      </span>
                    </div>
                  </div>
                );
              })}
              
              {upcomingDeadlines.length === 0 && (
                <div className="text-center py-4">
                  <Schedule className="text-gray-300 text-2xl mx-auto mb-2" />
                  <p className="text-gray-500 text-sm">Không có deadline nào</p>
                </div>
              )}
            </div>
          </motion.div>
        </div>

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="bg-white rounded-2xl shadow-xl p-6 mb-8"
        >
          <h3 className="text-lg font-bold text-gray-800 mb-4">Thao tác nhanh</h3>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="flex items-center space-x-3 p-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl hover:shadow-lg transition-all duration-300"
            >
              <WorkOutline className="text-xl" />
              <span className="font-medium">Xem cases</span>
            </motion.button>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="flex items-center space-x-3 p-4 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl hover:shadow-lg transition-all duration-300"
            >
              <Upload className="text-xl" />
              <span className="font-medium">Upload design</span>
            </motion.button>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="flex items-center space-x-3 p-4 bg-gradient-to-r from-yellow-500 to-orange-600 text-white rounded-xl hover:shadow-lg transition-all duration-300"
            >
              <Assessment className="text-xl" />
              <span className="font-medium">Báo cáo</span>
            </motion.button>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="flex items-center space-x-3 p-4 bg-gradient-to-r from-purple-500 to-pink-600 text-white rounded-xl hover:shadow-lg transition-all duration-300"
            >
              <Settings className="text-xl" />
              <span className="font-medium">Cài đặt</span>
            </motion.button>
          </div>
        </motion.div>

        {/* Monthly Performance Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.9 }}
          className="bg-white rounded-2xl shadow-xl p-6"
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-bold text-gray-800">Hiệu suất tháng này</h3>
            <div className="flex items-center space-x-2">
              <TrendingUp className="text-green-500" />
              <span className="text-sm text-green-600 font-medium">+12% so với tháng trước</span>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-xl font-bold">{stats.inProgressCases}</span>
              </div>
              <p className="text-sm text-gray-600">Cases đang làm</p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-xl font-bold">{stats.completedCases}</span>
              </div>
              <p className="text-sm text-gray-600">Cases hoàn thành</p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-yellow-500 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-xl font-bold">{formatCurrency(stats.totalEarnings)}</span>
              </div>
              <p className="text-sm text-gray-600">Thu nhập</p>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default DesignerDashboard; 